import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WishlistProvider } from './context/WishlistContext';
import { ChatProvider } from './context/ChatContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Lazy loaded pages
const Home          = lazy(() => import('./pages/Home'));
const Login         = lazy(() => import('./pages/Login'));
const Register      = lazy(() => import('./pages/Register'));
const CategoryListing = lazy(() => import('./pages/CategoryListing'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const PostAd        = lazy(() => import('./pages/PostAd'));
const UserProfile   = lazy(() => import('./pages/UserProfile'));
const Chat          = lazy(() => import('./pages/Chat'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const MyAds         = lazy(() => import('./pages/MyAds'));
const Wishlist      = lazy(() => import('./pages/Wishlist'));
const SearchResults = lazy(() => import('./pages/SearchResults'));
const Deals         = lazy(() => import('./pages/Deals'));
const Blog          = lazy(() => import('./pages/Blog'));
const NotFound      = lazy(() => import('./pages/NotFound'));
const AboutUs       = lazy(() => import('./pages/AboutUs'));
const Careers       = lazy(() => import('./pages/Careers'));
const HelpCenter    = lazy(() => import('./pages/HelpCenter'));
const ReportProblem = lazy(() => import('./pages/ReportProblem'));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
      <p className="text-sm text-gray-500 font-medium">Loading...</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <PageLoader />;
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const AdminRoute = ({ children }) => {
  const { isAdmin, isAuthenticated, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
};

const Layout = ({ children, noFooter = false }) => {
  const { isAdmin } = useAuth();
  return (
    <div className={`flex flex-col min-h-screen${isAdmin ? ' bg-rose-50/40' : ''}`}>
      <Navbar />
      <main className="flex-1 pt-16">
        {isAdmin && (
          <div className="sticky top-16 z-40 bg-red-600 text-white text-center py-1.5 text-xs font-bold tracking-wide shadow-sm">
            🛡️ ADMIN MODE ACTIVE &nbsp;—&nbsp; Administrator Account
          </div>
        )}
        {children}
      </main>
      {!noFooter && <Footer />}
    </div>
  );
};

const AppRoutes = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/" element={<Layout><Home /></Layout>} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/listings" element={<Layout><CategoryListing /></Layout>} />
      <Route path="/deals" element={<Layout><Deals /></Layout>} />
      <Route path="/blog" element={<Layout><Blog /></Layout>} />
      <Route path="/search" element={<Layout><SearchResults /></Layout>} />
      <Route path="/product/:id" element={<Layout><ProductDetail /></Layout>} />
      <Route path="/profile/:id" element={<Layout><UserProfile /></Layout>} />
      <Route path="/about" element={<Layout><AboutUs /></Layout>} />
      <Route path="/careers" element={<Layout><Careers /></Layout>} />
      <Route path="/help-center" element={<Layout><HelpCenter /></Layout>} />
      <Route path="/report-problem" element={<Layout><ReportProblem /></Layout>} />

      {/* Protected Routes */}
      <Route path="/post-ad" element={<ProtectedRoute><Layout><PostAd /></Layout></ProtectedRoute>} />
      <Route path="/post-ad/edit/:id" element={<ProtectedRoute><Layout><PostAd /></Layout></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><Layout noFooter><Chat /></Layout></ProtectedRoute>} />
      <Route path="/chat/:conversationId" element={<ProtectedRoute><Layout noFooter><Chat /></Layout></ProtectedRoute>} />
      <Route path="/my-ads" element={<ProtectedRoute><Layout><MyAds /></Layout></ProtectedRoute>} />
      <Route path="/wishlist" element={<ProtectedRoute><Layout><Wishlist /></Layout></ProtectedRoute>} />

      {/* Admin Routes */}
      <Route path="/admin/*" element={<AdminRoute><AdminDashboard /></AdminRoute>} />

      <Route path="*" element={<Layout><NotFound /></Layout>} />
    </Routes>
  </Suspense>
);

const App = () => (
  <Router>
    <AuthProvider>
      <WishlistProvider>
        <ChatProvider>
          <AppRoutes />
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 3500,
              style: { borderRadius: '12px', fontFamily: 'Inter, sans-serif', fontSize: '14px' },
              success: { iconTheme: { primary: '#6366f1', secondary: '#fff' } },
              error: { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
            }}
          />
        </ChatProvider>
      </WishlistProvider>
    </AuthProvider>
  </Router>
);

export default App;
